import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Modal } from '../../components/ui/Modal';
import { Cargando } from '../../components/ui/Estados';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import {
  repositorioUsuarios,
  type AccesoUsuario,
  type UsuarioAdmin,
} from '../../../data/repositories/RepositorioUsuarios';
import estilos from '../../components/layout/PaginaCrud.module.css';

interface PermisosUsuarioProps {
  usuario: UsuarioAdmin | null;
  guardando: boolean;
  onCerrar: () => void;
  onGuardar: (acceso: AccesoUsuario, verUbicaciones: boolean) => void;
}

/**
 * Permisos y alcance de un usuario.
 *
 * Un usuario ve por defecto los datos de su cliente. Aquí se le pueden dar
 * clientes adicionales —para quien atiende varias instalaciones— y acotarlo a
 * áreas concretas. Se separa del formulario de datos porque son decisiones de
 * naturaleza distinta: una es quién es la persona, otra qué puede alcanzar.
 */
export function PermisosUsuario({ usuario, guardando, onCerrar, onGuardar }: PermisosUsuarioProps) {
  const [clienteIds, setClienteIds] = useState<number[]>([]);
  const [areaIds, setAreaIds] = useState<number[]>([]);
  const [usarActuadores, setUsarActuadores] = useState(true);
  const [verUbicaciones, setVerUbicaciones] = useState(false);

  const detalle = useQuery({
    queryKey: ['usuario', usuario?.id, 'detalle'],
    queryFn: () => repositorioUsuarios.detalle(usuario?.id ?? ''),
    enabled: usuario !== null,
  });

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const areas = useQuery({
    queryKey: ['catalogo', 'areas', 'todas'],
    queryFn: () => repositorioCatalogos.todasLasAreas(),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!detalle.data || !usuario) return;

    setClienteIds(detalle.data.acceso.clienteIds);
    setAreaIds(detalle.data.acceso.areaIds);
    setUsarActuadores(detalle.data.acceso.puedeUsarActuadores);
    setVerUbicaciones(usuario.puedeVerUbicaciones);
  }, [detalle.data, usuario]);

  const esSuperadmin = usuario?.rol.esSuperadmin ?? false;

  const alternar = (lista: number[], id: number): number[] =>
    lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id];

  /** Clientes adicionales: el principal ya lo tiene por su perfil. */
  const clientesAdicionales = useMemo(
    () => (clientes.data ?? []).filter((c) => c.id !== usuario?.clienteId),
    [clientes.data, usuario?.clienteId],
  );

  if (!usuario) return null;

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      ancho
      titulo={`Permisos de ${usuario.nombre}`}
      descripcion="Qué puede alcanzar y qué puede accionar."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            cargando={guardando}
            icono="guardar"
            onClick={() =>
              onGuardar(
                { clienteIds, areaIds, puedeUsarActuadores: usarActuadores },
                verUbicaciones,
              )
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      {detalle.isLoading ? (
        <Cargando etiqueta="Cargando permisos" />
      ) : (
        <div className={estilos.formulario}>
          <div>
            <strong>Qué puede hacer</strong>

            <label className={estilos.opcion} style={{ marginTop: '0.6rem' }}>
              <input
                type="checkbox"
                checked={esSuperadmin || usarActuadores}
                disabled={esSuperadmin}
                onChange={(e) => setUsarActuadores(e.target.checked)}
              />
              <span>
                <strong>Accionar equipos</strong>
                <span className={estilos.ayuda}>
                  {esSuperadmin
                    ? 'Un superadministrador siempre puede accionar equipos.'
                    : 'Permite encender, apagar y dar pulsos desde el centro de control.'}
                </span>
              </span>
            </label>

            <label className={estilos.opcion} style={{ marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                checked={esSuperadmin || verUbicaciones}
                disabled={esSuperadmin}
                onChange={(e) => setVerUbicaciones(e.target.checked)}
              />
              <span>
                <strong>Ver la ubicación de otras personas</strong>
                <span className={estilos.ayuda}>
                  Sin esto sigue entrando al mapa, pero sólo se ve a sí mismo.
                </span>
              </span>
            </label>
          </div>

          {!esSuperadmin && (
            <>
              <div>
                <strong>Clientes adicionales</strong>
                <p className={estilos.ayuda}>
                  Además de {usuario.clienteNombre ?? 'su cliente'}, que ya tiene asignado. Útil
                  para quien atiende varias instalaciones.
                </p>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.4rem',
                    marginTop: '0.5rem',
                  }}
                >
                  {clientesAdicionales.length === 0 ? (
                    <span className={estilos.ayuda}>No hay otros clientes registrados.</span>
                  ) : (
                    clientesAdicionales.map((c) => (
                      <label key={c.id} className={estilos.opcion}>
                        <input
                          type="checkbox"
                          checked={clienteIds.includes(c.id)}
                          onChange={() => setClienteIds((prev) => alternar(prev, c.id))}
                        />
                        <span>{c.nombre}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <strong>Acotar a ciertas áreas</strong>
                <p className={estilos.ayuda}>
                  {areaIds.length === 0
                    ? 'Sin marcar ninguna, ve todas las áreas de los clientes que alcanza.'
                    : `Sólo verá las ${areaIds.length} área(s) marcadas.`}
                </p>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.4rem',
                    marginTop: '0.5rem',
                    maxHeight: '14rem',
                    overflowY: 'auto',
                  }}
                >
                  {(areas.data ?? []).map((a) => (
                    <label key={a.id} className={estilos.opcion}>
                      <input
                        type="checkbox"
                        checked={areaIds.includes(a.id)}
                        onChange={() => setAreaIds((prev) => alternar(prev, a.id))}
                      />
                      <span>{a.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {esSuperadmin && (
            <p className={estilos.ayuda}>
              <Icon nombre="infoContorno" tamano={14} /> Un superadministrador alcanza todos los
              clientes y áreas, así que no hay nada que acotar.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
